/**
 * Shared device + session helpers for login and concurrent-session flows.
 */

/** Grace after manual login — matches App.js auth listener. */
export const AUTH_FLOW_WINDOW_MS = 2 * 60 * 1000;
/** Grace while server sessionId/deviceId propagate to Firestore. */
export const SESSION_CHECK_GRACE_MS = 45 * 1000;

export function isFreshManualAuthFlow() {
  const authFlowAt = parseInt(localStorage.getItem("authFlowAt") || "0", 10);
  return !!authFlowAt && Date.now() - authFlowAt < AUTH_FLOW_WINDOW_MS;
}

export function isWithinSessionPropagationGrace() {
  const sessionSyncAt = parseInt(localStorage.getItem("sessionSyncAt") || "0", 10);
  return !!sessionSyncAt && Date.now() - sessionSyncAt < SESSION_CHECK_GRACE_MS;
}

/** Suppress 401/cross-tab/idle forced logouts during login or session sync. */
export function shouldSuppressForcedLogout() {
  return isFreshManualAuthFlow() || isWithinSessionPropagationGrace();
}

export function resolveServerSessionId(sessionResponse) {
  return (
    sessionResponse?.sessionId ||
    sessionResponse?.session?.id ||
    sessionResponse?.session?.sessionId ||
    sessionResponse?.data?.sessionId ||
    sessionResponse?.data?.session?.id ||
    sessionResponse?.data?.session?.sessionId
  );
}

export function markFreshAuthFlow(sessionId) {
  const now = String(Date.now());
  if (sessionId) {
    localStorage.setItem("sessionId", sessionId);
  } else {
    localStorage.removeItem("sessionId");
  }
  localStorage.setItem("sessionSyncAt", now);
  localStorage.setItem("authFlowAt", now);
  localStorage.removeItem("forceLoginPending");
}

function generateDeviceFingerprint() {
  const parts = [
    typeof navigator !== "undefined" ? navigator.platform : "",
    typeof navigator !== "undefined" ? navigator.userAgent : "",
    typeof navigator !== "undefined" ? navigator.hardwareConcurrency : "",
    typeof window !== "undefined" ? window.screen.width : 0,
    typeof window !== "undefined" ? window.screen.height : 0,
    typeof window !== "undefined" ? window.screen.colorDepth : 0,
    new Date().getTimezoneOffset(),
  ];

  const raw = parts.join("|");
  let hash = 0;
  for (let i = 0; i < raw.length; i++) {
    const chr = raw.charCodeAt(i);
    hash = ((hash << 5) - hash) + chr;
    hash |= 0;
  }
  return "DEV-" + Math.abs(hash).toString(36);
}

export function parseUserAgent() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  let browser = "Unknown";
  let os = "Unknown";
  let deviceType = "desktop";
  if (/Chrome/i.test(ua) && !/Edge/i.test(ua)) browser = "Chrome";
  else if (/Firefox/i.test(ua)) browser = "Firefox";
  else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = "Safari";
  else if (/Edge/i.test(ua)) browser = "Edge";
  if (/Windows/i.test(ua)) os = "Windows";
  else if (/Mac/i.test(ua)) os = "macOS";
  else if (/Linux/i.test(ua)) os = "Linux";
  else if (/Android/i.test(ua)) os = "Android";
  else if (/iOS|iPhone|iPad/i.test(ua)) os = "iOS";
  if (/Mobile/i.test(ua) && !/iPad/i.test(ua)) deviceType = "mobile";
  else if (/Tablet|iPad/i.test(ua)) deviceType = "tablet";
  return { browser, os, deviceType };
}

export async function getDeviceId() {
  let deviceId = localStorage.getItem("deviceId");
  if (!deviceId) {
    deviceId = generateDeviceFingerprint();
    localStorage.setItem("deviceId", deviceId);
  }
  return deviceId;
}

export async function getDeviceInfo() {
  const { browser, os, deviceType } = parseUserAgent();
  const deviceId = await getDeviceId();
  return {
    deviceId,
    browser,
    os,
    deviceType,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
  };
}
